function metricValue(summary, key) {
  const value = summary && summary[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function formatMetric(value, unit, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—'
  const number = Number(value)
  if (unit === 'bytes') {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let index = 0
    let converted = number
    while (converted >= 1024 && index < units.length - 1) {
      converted /= 1024
      index += 1
    }
    return `${converted.toFixed(index ? 1 : 0)} ${units[index]}`
  }
  return `${number.toFixed(digits)}${unit ? ` ${unit}` : ''}`
}

function latencyBand(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'neutral'
  if (value < 200) return 'green'
  if (value < 500) return 'yellow'
  if (value < 1000) return 'orange'
  if (value < 3000) return 'red'
  return 'burgundy'
}

function formatCount(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—'
  return Math.round(Number(value)).toLocaleString('en-US')
}

function pointsFor(series, key, labelFilter, transform = value => value) {
  const groups = (series && series[key]) || []
  const matching = labelFilter ? groups.filter(item => labelFilter(item.labels || {})) : groups
  return matching.reduce((points, item) => {
    return points.concat((item.points || []).map(point => [
      point.timestamp * 1000,
      point.value === null ? null : transform(point.value)
    ]))
  }, []).sort((left, right) => left[0] - right[0])
}

function hasValues(series, keys, labelFilter) {
  return keys.some(key => pointsFor(series, key, labelFilter)
    .some(point => point[1] !== null && Number.isFinite(Number(point[1]))))
}

// Queues whose length was above zero at some point in the period; the rest are
// listed in the table only, so the chart is not buried under flat zero lines.
function activeQueues(series, key = 'queue_length_maximum') {
  return ((series && series[key]) || [])
    .filter(item => (item.points || []).some(point => point.value !== null && Number(point.value) > 0))
    .map(item => (item.labels || {}).queue)
    .filter(Boolean)
    .sort()
}

// Busiest queues first, then by name, so a growing backlog is at the top.
function sortQueueRows(rows) {
  return (rows || []).slice().sort((left, right) => (
    (right.currentLength || 0) - (left.currentLength || 0) ||
    (right.maximumLength || 0) - (left.maximumLength || 0) ||
    left.queue.localeCompare(right.queue)
  ))
}

// RabbitMQ health from the dashboard's `rabbitmq` block; `up` is null when the
// state is unknown (no scrape series, or Prometheus did not answer).
function rabbitmqStatus(rabbitmq) {
  const up = rabbitmq ? rabbitmq.up : null
  if (up === true) {
    return {
      tone: 'green',
      label: `${formatMetric(rabbitmq.availabilityPercent, '%')} available`,
      text: 'The broker answers Prometheus scrapes.'
    }
  }
  if (up === false) {
    return {
      tone: 'red',
      label: 'Unavailable',
      text: 'The broker does not answer Prometheus scrapes. Event delivery is stalled until it is back.'
    }
  }
  return {
    tone: 'neutral',
    label: 'Unknown',
    text: 'Prometheus has no RabbitMQ state for this period.'
  }
}

function availableGranularities(durationSeconds, options, maximumPoints = 1000) {
  return options.filter(option => option.seconds === null || (
    durationSeconds > 0 && Math.ceil(durationSeconds / option.seconds) <= maximumPoints
  ))
}

module.exports = {
  activeQueues,
  availableGranularities,
  formatCount,
  formatMetric,
  hasValues,
  latencyBand,
  metricValue,
  pointsFor,
  rabbitmqStatus,
  sortQueueRows
}
